<template>
  <div class="soar-container !p-0 flex flex-col gap-y-4">
    <FeesTableSkeleton v-if="isLoading" />
    <template v-else>
      <div :key="businessKey" v-for="(businessItem, businessKey) in businessLogicList">
        <div
          v-if="businessItem.errors.filter((e) => e.type != noteErrorType)?.length > 0"
          class="rounded border border-brand-gray-300 py-2 px-4 flex flex-col gap-y-2"
        >
          <h4 class="text-lg">{{ $t('validationBusienssRulesHeader') }}</h4>
          <div class="flex flex-col gap-y-2">
            <div
              :key="key"
              v-for="(error, key) in businessItem.errors.filter((e) => e.type !== noteErrorType)"
              class="w-full px-4 py-3 flex items-center gap-x-3 rounded border border-brand-gray-600 bg-brand-gray-300"
            >
              <faCircleXMark class="w-6 fill-brand-red" />
              <h5>
                {{ error.message }}
                <small class="text-xs ltr:font-sans text-brand-black-autofill"
                  >({{ error.code }})</small
                >
              </h5>
            </div>
          </div>
          <div class="flex items-center gap-x-1 text-xs ltr:font-sans">
            <span>{{ $t('openTicketMsg') }}</span>
            <a :href="reportTicketHRef" target="_blank" class="text-primary font-medium">
              {{ $t('here') }}
            </a>
          </div>
        </div>
        <template v-else>
          <div v-if="businessItem.notes?.length > 0" class="py-6 flex flex-col gap-y-2">
            <div
              :key="key"
              v-for="(note, key) in businessItem.notes"
              class="p-3 animate-pulse border border-dashed border-red-600 ltr:font-sans text-xs"
            >
              <p class="">{{ note.message }}</p>
            </div>
          </div>
          <table
            :key="key"
            v-for="(table, key) in businessItem.tables"
            class="w-full table-auto border-collapse border text-sans"
          >
            <thead class="bg-brand-gray-150">
              <tr class="py-2 px-3 border border-brand-gray-300 text-start">
                <th colspan="4" class="text-xs py-2 px-3 border border-brand-gray-300 text-start">
                  {{ table.serviceName }}
                </th>
              </tr>
              <tr class="py-2 px-3 border border-brand-gray-300 text-start text-sm">
                <th class="py-2 px-3 border border-brand-gray-300 text-start">
                  {{ $t('feesDescription') }}
                </th>
                <th class="py-2 px-3 border border-brand-gray-300 text-end">{{ $t('amount') }}</th>
                <th
                  v-if="table.containsVat"
                  class="py-2 px-3 border border-brand-gray-300 text-end"
                >
                  {{ $t('vatAmount') }}
                </th>
                <th
                  v-if="table.containsVat"
                  class="py-2 px-3 border border-brand-gray-300 text-end"
                >
                  {{ $t('totalWithVat') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                :key="key"
                v-for="(row, key) in table.rows"
                class="text-[12px] tracking-tight ltr:font-sans even:bg-brand-gray-150"
              >
                <td class="py-2 px-3 border border-brand-gray-300 text-start">
                  {{ row.localizedName }}
                </td>
                <td class="py-2 px-3 border border-brand-gray-300 text-end">
                  {{ row.amountPretty || '--' }}
                </td>
                <template v-if="table.containsVat">
                  <td v-if="row.vatAmount" class="py-2 px-3 border border-brand-gray-300 text-end">
                    {{ row.vatPretty }}
                  </td>
                  <td v-else class="py-2 px-3 border border-brand-gray-300 text-end">
                    <div class="w-full h-full flex justify-end items-center gap-x-1">
                      <span class="line-through">0.00</span>
                      <span class="font-mono text-[10px] tracking-tighter"
                        >({{ $t('vatFree') }})</span
                      >
                    </div>
                  </td>
                  <td class="py-2 px-3 border border-brand-gray-300 text-end">
                    {{ row.rowTotalPretty }}
                  </td>
                </template>
              </tr>
              <tr class="bg-brand-grayblue-300">
                <td class="py-2 px-3" colspan="3">
                  {{ $t('Total') }}
                </td>
                <td class="py-2 px-3 text-end ltr:font-sans font-medium text-xs">
                  {{ intlNumberFormat.format(table.totalWithVat) }}
                </td>
              </tr>
              <tr v-if="showRefundLine">
                <td
                  v-if="
                    table.refundableAmountForRejected > 0 || table.refundableAmountForApproved > 0
                  "
                  class="py-1 px-3 text-xs text-brand-black-autofill"
                  colspan="4"
                >
                  <p v-if="table.refundableAmountForRejected > 0">
                    {{
                      `${$t('refundAmountDescription')} ${table.refundableAmountForRejected} AED`
                    }}
                  </p>
                  <p v-if="table.refundableAmountForApproved > 0">
                    {{
                      `${$t('refundAmountDescriptionForApproved')} ${table.refundableAmountForApproved} AED`
                    }}
                  </p>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div v-if="businessLogic.data" class="flex flex-col gap-y-12">
        <div class="flex flex-col gap-y-2">
          <h5 class="text-lg">{{ $t('payBy') }}</h5>
          <div class="flex gap-x-1">
            <input
              type="checkbox"
              name="hasAgreed"
              v-model="hasAgreed"
              class="accent-primary-700"
            />
            <label for="hasAgreed" class="pt-0.5">{{ $t('iAgree') }}</label>
            <a href="#" target="_blank" class="pt-0.5 text-primary font-medium">
              {{ $t('TermsConditions') }}
            </a>
          </div>
          <FeesStepPaymentMethods
            :key="feesTableConfig.selectedPaymentMethod"
            :has-agreed="hasAgreed"
            :selected-payment-method="feesTableConfig.selectedPaymentMethod"
            class="flex gap-x-2"
            @onSelection="handlePaymentMethodChange"
          />
          <UiModal v-model="showPayLaterModal" :title="$t('confirmation')">
            <template #bodyContent>
              {{ $t('confirmationPayLaterMSG') }}
            </template>
            <template #footerContent>
              <div class="w-full py-2 flex justify-between items-center">
                <button
                  class="px-1.5 py-0.5 rounded-md bg-brand-gray-600 text-white flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
                  type="submit"
                  @click="onPayLaterResponse(false)"
                >
                  <faTimes class="w-3 fill-white"></faTimes>
                  {{ $t('close') }}
                </button>
                <button
                  class="px-1.5 py-0.5 rounded-md bg-gradient-to-tr from-linear-s to-linear-e text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
                  type="submit"
                  @click="onPayLaterResponse(true)"
                >
                  {{ $t('change') }}
                </button>
              </div>
            </template>
          </UiModal>
        </div>
        <FeesStepPaymentTable
          v-if="
            !isFetchingNewPaymentMethod &&
            hasAgreed &&
            ['eWallet', 'creditCard'].includes(feesTableConfig.selectedPaymentMethod)
          "
          :fees-config="feesTableConfig"
          :business-logic="businessLogicList"
        ></FeesStepPaymentTable>
      </div>
    </template>
    <div class="flex justify-between">
      <FilledButton class="px-5 py-1.5 text-white" @click="$emit('onBack')">
        {{ $t('back') }}
      </FilledButton>
      <FilledButton
        v-if="['eWallet', 'creditCard'].includes(feesTableConfig.selectedPaymentMethod)"
        :disabled="!hasAgreed || !feesTableConfig.selectedPaymentMethod"
        class="text-white px-5 py-1.5"
        fill-class="bg-gradient-to-b from-red-600 to-red-800"
        @click="initiatePaymentGateway"
      >
        {{ $t('Pay') }}
      </FilledButton>
    </div>
  </div>
</template>

<script setup>
import faCircleXMark from '@/assets/icons/faCircleXMark.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import client_config from '@/assets/json/client_config.json'
import FilledButton from '@/components/Buttons/Filled.vue'
import DraftDialog from '@/components/search/Services/DraftDialog.vue'
import FeesTableSkeleton from '@/components/skeletons/FeesTableSkeleton.vue'
import axios from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useFormStore } from '@/stores/form'
import { useMultiplePayStore } from '@/stores/multiplePay'
import FeesStepPaymentMethods from '@/views/service-form/FeesStep/FeesStepPaymentMethods.vue'
import FeesStepPaymentTable from '@/views/service-form/FeesStep/FeesStepPaymentTable.vue'
import { notify } from 'notiwind'
import { computed, onMounted, reactive, ref, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import UiModal from '@/components/Ui/Modal/index.vue'
import faTimes from '@/assets/icons/faTimes.svg?component'

const emit = defineEmits('onChangeState')

const i18n = useI18n()
const route = useRoute()
const router = useRouter()
const formStore = useFormStore()
const authStore = useAuthStore()
const multiplePayStore = useMultiplePayStore()

const isEligibleForTokenization = ref(false)
const isLoading = ref(true)
const isFetchingNewPaymentMethod = ref(false)
const businessLogic = ref({})
const hasAgreed = ref(false)
const showPayLaterModal = ref(false)
const businessLogicList = ref([])
const massPaymentDraftsNumber = ref([])

const {
  errorType: { note: noteErrorType }
} = client_lookups

// 'AE-en', { style: 'currency', currency: 'AED' }
const intlNumberFormat = Intl.NumberFormat()

const {
  params: { serviceId }
} = route

const multiplePaymentOptions = reactive({
  drafts: [],
  paymentType: ''
})

const props = defineProps({
  isMultiple: {
    type: Boolean,
    default: false
  }
})

const feesTableConfig = reactive({
  showExemptionReason: false,
  enableAmwalCreditCardForServiceCenter: client_config.enableAmwalCreditCardForServiceCenter,
  POSPaymentEnabled: false,
  selectedPaymentMethod: null
})

const reportTicketHRef = computed(() => {
  const { requestNumber } = formStore.draft || {}
  return requestNumber && serviceId ? `/ticket/${requestNumber}/${serviceId}` : '#' // Fallback in case of missing values
})

const isUserServiceCenter = computed(() => {
  const {
    application: { serviceCenters }
  } = client_lookups
  return authStore.user.applicationId == serviceCenters
})

const paymentType = computed(() => {
  switch (feesTableConfig.selectedPaymentMethod) {
    case 'creditCard':
      return 5
    case 'eWallet':
    default:
      return 2
  }
})

const showRefundLine = computed(() => {
  const {
    ServiceTransactions: { refundFees, refundDeposit },
    icaServiceTransactions
  } = client_lookups

  return ![refundFees, refundDeposit, ...icaServiceTransactions].includes(serviceId)
})

const multipleDataReset = () => {
  businessLogicList.value = []
  massPaymentDraftsNumber.value = []
  multiplePaymentOptions.drafts = []
}

const multipleCheck = async () => {
  const { enableValidateDraftAPI, enableOdmConsultation } = client_config
  if (!multiplePayStore.drafts.length) {
    notify(
      {
        group: 'error',
        title: '',
        text: i18n.t('pleaseSelectReadyToPayDraft')
      },
      6000
    )
    router.back()
  }

  multiplePayStore.drafts.map((item) => multiplePaymentOptions.drafts.push(item.requestNumber))
  for (let index = 0; index < multiplePayStore.drafts.length; index++) {
    multiplePayStore.drafts[index] = {
      ...multiplePayStore.drafts[index],
      allowedOptionalIds: [],
      commission: 0,
      refundAmount: 0
    }
    massPaymentDraftsNumber.value.push(multiplePayStore.drafts[index].requestNumber)
    if (enableValidateDraftAPI) {
      await getBusinessRules(multiplePayStore.drafts[index])
    }
    if (enableOdmConsultation) {
      //TODO
    }
  }
}

const fetchData = async () => {
  multipleDataReset()
  if (props.isMultiple) {
    await multipleCheck()
  } else {
    await getBusinessRules(formStore.draft)
  }
}

onMounted(async () => {
  await fetchData()
  await getTokenEligibility()

  if (isUserServiceCenter.value) {
    await getEnablePOSPaymentMethodFlag()
  }
  isLoading.value = false
})

const getTokenEligibility = async () => {
  isEligibleForTokenization.value = await axios.get('/api/payment/eligibleForTokenizationPayment')
}

const getBusinessRules = async (draft) => {
  isFetchingNewPaymentMethod.value = true

  const payload = {
    drafts: [
      {
        draftNumber: draft.requestNumber,
        includeOptionallIds: draft?.allowedOptionalIds?.map((item) => item.id)
      }
    ],
    massPaymentDraftsNumber: massPaymentDraftsNumber.value,
    paymentType: paymentType.value
  }

  businessLogic.value = await axios.post('/api/requestmaster/validate/businessRules', payload)

  const actualErrors = businessLogic.value.errors.filter((e) => e.type !== noteErrorType)

  if (actualErrors.length > 0) {
    businessLogic.value.errors
      .filter((e) => e.type !== noteErrorType)
      .forEach((e) => {
        notify(
          {
            group: 'error',
            title: i18n.t('errorSetDefaultAddress'),
            text: e.message
          },
          6000
        )
      })
    businessLogicList.value.push(businessLogic.value)
    return
  }

  businessLogic.value = {
    ...businessLogic.value,
    notes: businessLogic.value.errors.filter((e) => e.type === noteErrorType),
    tables: businessLogic.value.data.requestFeeSetupResult.map((table) => {
      return {
        ...table,
        rows: table.result.map((r) => {
          const row = {
            ...r,
            localizedName: i18n.locale.value ? r.englishName : r.arabicName,
            vatPretty: intlNumberFormat.format(r.vatAmount),
            amountPretty: intlNumberFormat.format(r.amount),
            rowTotal: +r.vatAmount + +r.amount
          }

          row.rowTotalPretty = intlNumberFormat.format(row.rowTotal)

          return row
        })
      }
    })
  }
  businessLogicList.value.push(businessLogic.value)
  performTableConfig(businessLogic.value, draft)

  await performRSARelatedTasks(payload)
  isFetchingNewPaymentMethod.value = false
}

const handlePaymentMethodChange = async (method) => {
  feesTableConfig.selectedPaymentMethod = method
  if (method == 'payLater') {
    // TODO: show confirm
    showPayLaterModal.value = true
    return
  }

  if (method == 'eWallet') {
    const bankAccounts = await axios.get('/api/payment/bankAccounts')
    console.info('TODO: process bank accounts into the dropdown', bankAccounts)
  }

  await fetchData()
}

const getEnablePOSPaymentMethodFlag = async () => {
  const isEnablePOSPaymentMethodFlag = await axios.get(
    '/api/serviceCenterPOS/isEnablePOSPaymentMethodFlag'
  )
  if (!isEnablePOSPaymentMethodFlag) {
    return
  }
  feesTableConfig.POSPaymentEnabled = true

  const hasDevice = await axios.get('/api/serviceCenterPOS/isDeviceSelected')
  const { isConnected } = hasDevice

  feesTableConfig.redirectToPOSDeviceFlag = hasDevice ? !isConnected : true

  if (feesTableConfig.redirectToPOSDeviceFlag) {
    console.info('TODO: show warning message')
  }
}

const performTableConfig = async (response, draft) => {
  const {
    ServiceTransactions: { dubaiResidentsNewBorn }
  } = client_lookups

  const { allowedAdministrativeRegionsForIcaExcemptions } = client_config

  response.data.requestFeeSetupResult.forEach((fee, index) => {
    const {
      request: { personUnifiedNumber, autoExemptionReasonIds = [] },
      administrativeRegionId
    } = draft

    const isEligibleForExemption = fee.isICARequestEligibleForExemption
    const isResidentAndHasUnifiedNumber =
      !!personUnifiedNumber && serviceId == dubaiResidentsNewBorn

    const isIcaExemptionsEnabled =
      allowedAdministrativeRegionsForIcaExcemptions.includes(administrativeRegionId)

    if (isEligibleForExemption && isResidentAndHasUnifiedNumber && isIcaExemptionsEnabled) {
      if (autoExemptionReasonIds.length > 0) {
        // TODO: await load excemptionReasons
        feesTableConfig.showExemptionReason = true
      }

      if (fee.result.some((r) => !r.exempted)) {
        feesTableConfig.showExemptionLink = true
      } else {
        Object.assign(feesTableConfig, {
          showExemptionLink: false,
          hideEDirhamReceiptSection: true,
          hidePOsSection: true
        })
      }
    } else {
      if (fee.result.some((r) => !r.exempted)) {
        Object.assign(feesTableConfig, {
          hideEDirhamReceiptSection: true,
          hidePOsSection: true
        })
      }
    }
  })
}

const performRSARelatedTasks = async (payload) => {
  const {
    application: { TypingCenter, establishment }
  } = client_lookups

  const requiresRSAConfig = [TypingCenter, establishment].includes(authStore.user.applicationId)
  if (requiresRSAConfig) {
    const rsaConfig = await axios.post('/api/payment/verifyRSAConfigration', {
      multiplePayment: payload
    })

    const { isRequiredPaymentToken, isRequiredServiceToken } = rsaConfig

    const isRequiredToken = isRequiredPaymentToken || isRequiredServiceToken
    feesTableConfig.hidePaymentSection = isRequiredToken
    feesTableConfig.showRsaForm = isRequiredToken
  } else {
    feesTableConfig.hidePaymentSection = false
  }
}

const onPayLaterResponse = (response) => {
  if (!response) {
    showPayLaterModal.value = false
    return
  }

  router.push({ name: 'EstRequestsForPayment' })
}

onUnmounted(() => {
  multiplePayStore.$reset()
})
</script>

<style lang="scss" scoped></style>
